import type { Plan } from "../plans/types";

export type HudState = {
  score: number;
  timeLeft: number;
  bricks: number;
  hint?: string;
};

export type TouchHandlers = {
  onPlace: () => void;
  onSize: () => void;
  onColor: () => void;
  onPlan: (id: string) => void;
};

function fmtTime(seconds: number): string {
  const s = Math.max(0, Math.ceil(seconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}

function bindTap(node: HTMLElement, cb: (e: Event) => void) {
  const fn = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
    cb(e);
  };
  node.addEventListener("pointerdown", fn);
  return () => node.removeEventListener("pointerdown", fn);
}

export class Hud {
  private root: HTMLElement;
  private scoreEl: HTMLElement;
  private timeEl: HTMLElement;
  private bricksEl: HTMLElement;
  private hintEl: HTMLElement;
  private title: HTMLElement;
  private end: HTMLElement;
  private finalScore: HTMLElement;
  private endMessage: HTMLElement;
  private qualityEl: HTMLElement | null;
  private labelScore: HTMLElement | null;
  private labelTime: HTMLElement | null;
  private labelBricks: HTMLElement | null;
  private headline: HTMLElement | null;
  private tagline: HTMLElement | null;
  private touch: HTMLElement;
  private planStrip: HTMLElement;
  private planLabel: HTMLElement | null;
  private roomInput: HTMLInputElement | null;
  private netLine: HTMLElement | null;
  private lobby: HTMLElement | null;
  private lobbyTitle: HTMLElement | null;

  constructor() {
    this.root = el("hud");
    this.scoreEl = el("hud-score");
    this.timeEl = el("hud-time");
    this.bricksEl = el("hud-bricks");
    this.hintEl = el("hud-hint");
    this.title = el("title-screen");
    this.end = el("end-screen");
    this.finalScore = el("final-score");
    this.endMessage = el("end-message");
    this.qualityEl = document.getElementById("hud-quality");
    this.labelScore = document.getElementById("label-score");
    this.labelTime = document.getElementById("label-time");
    this.labelBricks = document.getElementById("label-bricks");
    this.headline = document.getElementById("game-title");
    this.tagline = document.getElementById("game-tagline");
    this.touch = el("touch-ui");
    this.planStrip = el("plan-strip");
    this.planLabel = document.getElementById("plan-active");
    this.roomInput = document.getElementById("room-code") as HTMLInputElement | null;
    this.netLine = document.getElementById("net-line");
    this.lobby = document.getElementById("lobby");
    this.lobbyTitle = document.getElementById("lobby-title");
  }

  roomCode(): string {
    return (this.roomInput?.value ?? "").trim().toUpperCase();
  }

  setRoomCode(code: string) {
    if (this.roomInput) this.roomInput.value = code;
  }

  setNetLine(text: string) {
    if (this.netLine) this.netLine.textContent = text;
  }

  setLobby(opts: { state?: "connecting" | "ready" | "error"; title?: string; line?: string }) {
    if (opts.state && this.lobby) this.lobby.dataset.state = opts.state;
    if (opts.title && this.lobbyTitle) this.lobbyTitle.textContent = opts.title;
    if (opts.line !== undefined) this.setNetLine(opts.line);
  }

  onHost(cb: () => void) {
    bindTap(el("btn-host"), cb);
  }

  onShare(cb: () => void) {
    bindTap(el("btn-share"), cb);
  }

  setStartLabel(text: string) {
    const b = document.getElementById("btn-start");
    if (b) b.textContent = text;
  }

  setCopy(opts: {
    title?: string;
    tagline?: string;
    scoreLabel?: string;
    timeLabel?: string;
    bricksLabel?: string;
  }) {
    if (opts.title && this.headline) this.headline.textContent = opts.title;
    if (opts.tagline && this.tagline) this.tagline.innerHTML = opts.tagline;
    if (opts.scoreLabel && this.labelScore) this.labelScore.textContent = opts.scoreLabel;
    if (opts.timeLabel && this.labelTime) this.labelTime.textContent = opts.timeLabel;
    if (opts.bricksLabel && this.labelBricks) this.labelBricks.textContent = opts.bricksLabel;
  }

  setChrome(opts: { quality?: string }) {
    if (opts.quality && this.qualityEl) this.qualityEl.textContent = opts.quality;
  }

  setPlans(plans: Plan[]) {
    this.planStrip.replaceChildren();
    for (const plan of plans) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "plan-chip";
      btn.dataset.plan = plan.id;
      btn.innerHTML = `<span class="plan-name">${plan.name}</span><span class="plan-n">${plan.bricks.length}</span>`;
      this.planStrip.appendChild(btn);
    }
  }

  setActivePlan(name: string, id?: string) {
    if (this.planLabel) this.planLabel.textContent = name;
    for (const chip of this.planStrip.querySelectorAll<HTMLElement>(".plan-chip")) {
      chip.classList.toggle("active", id ? chip.dataset.plan === id : chip.dataset.plan === name);
    }
  }

  bindTouch(h: TouchHandlers): () => void {
    const offs: Array<() => void> = [];
    offs.push(bindTap(el("btn-place"), () => h.onPlace()));
    offs.push(bindTap(el("btn-size"), () => h.onSize()));
    offs.push(bindTap(el("btn-color"), () => h.onColor()));
    const onStrip = (e: Event) => {
      const t = (e.target as HTMLElement).closest(".plan-chip") as HTMLElement | null;
      if (!t?.dataset.plan) return;
      e.preventDefault();
      e.stopPropagation();
      for (const chip of this.planStrip.querySelectorAll(".plan-chip")) {
        chip.classList.toggle("active", chip === t);
      }
      h.onPlan(t.dataset.plan);
    };
    this.planStrip.addEventListener("pointerdown", onStrip);
    offs.push(() => this.planStrip.removeEventListener("pointerdown", onStrip));
    return () => offs.forEach((fn) => fn());
  }

  setTouchVisible(on: boolean) {
    this.touch.classList.toggle("hidden", !on);
  }

  showTitle() {
    this.title.classList.remove("hidden");
    this.end.classList.add("hidden");
    this.root.classList.add("hidden");
    this.setTouchVisible(false);
  }

  showPlaying() {
    this.title.classList.add("hidden");
    this.end.classList.add("hidden");
    this.root.classList.remove("hidden");
  }

  showEnd(score: number, message: string) {
    this.root.classList.add("hidden");
    this.touch.classList.add("hidden");
    this.title.classList.add("hidden");
    this.end.classList.remove("hidden");
    this.finalScore.textContent = String(score);
    this.endMessage.textContent = message;
  }

  update(state: HudState) {
    this.scoreEl.textContent = String(state.score);
    this.timeEl.textContent = fmtTime(state.timeLeft);
    this.bricksEl.textContent = String(state.bricks);
    if (state.hint !== undefined) this.hintEl.textContent = state.hint;
  }

  onStart(cb: () => void) {
    bindTap(el("btn-start"), cb);
  }

  onRetry(cb: () => void) {
    bindTap(el("btn-retry"), cb);
  }
}

function el(id: string): HTMLElement {
  const node = document.getElementById(id);
  if (!node) throw new Error(`#${id} missing`);
  return node;
}
